import { useState, useCallback } from 'react';
import { admin } from '../../api/client.js';
import { useApi } from '../../hooks/useApi.js';
import { Badge } from '../../components/Badge.js';
import { Modal } from '../../components/Modal.js';
import { LoadingSpinner } from '../../components/LoadingSpinner.js';
import { ErrorMessage } from '../../components/ErrorMessage.js';
import type { AdminRole } from '../../types/index.js';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'orphaned', label: 'Orphaned' },
  { value: 'expired_active', label: 'Expired but Active' },
];

export function AdminRolesPage() {
  const [filter, setFilter] = useState('all');
  const [extendTarget, setExtendTarget] = useState<AdminRole | null>(null);
  const [extendDays, setExtendDays] = useState(30);
  const [actionLoading, setActionLoading] = useState(false);

  const fetcher = useCallback(() => admin.roles(filter), [filter]);
  const { data, loading, error, refetch } = useApi(fetcher, [filter]);

  const roles = data?.roles ?? [];

  const handleDeactivate = async (roleId: string) => {
    setActionLoading(true);
    try {
      await admin.deactivateRole(roleId);
      void refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to deactivate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!extendTarget) return;
    setActionLoading(true);
    try {
      await admin.extendRole(extendTarget.id, extendDays);
      setExtendTarget(null);
      void refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to extend');
    } finally {
      setActionLoading(false);
    }
  };

  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Role Assignments</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Manage Discord role assignments across all servers</p>
        </div>
      </div>

      <div className="filter-row">
        {FILTERS.map((f) => (
          <div
            key={f.value}
            className={`filter-pill ${filter === f.value ? 'active' : ''}`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </div>
        ))}
      </div>

      {loading && <LoadingSpinner text="Loading roles..." />}

      {!loading && roles.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--t3)', padding: '40px 0', textAlign: 'center' }}>
          {filter === 'all' ? 'No role assignments found' : `No ${filter.replace('_', ' ')} roles found`}
        </div>
      )}

      {!loading && roles.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Server</th>
                <th>User</th>
                <th>Plan</th>
                <th>Active</th>
                <th>Session</th>
                <th>Assigned</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => {
                const isExpired = r.active && new Date(r.expiresAt) < new Date();
                const isOrphaned = r.active && r.sessionState && r.sessionState !== 'COMPLETED';
                return (
                  <tr key={r.id} style={isExpired || isOrphaned ? { background: 'rgba(231,76,60,0.05)' } : undefined}>
                    <td style={{ color: 'var(--t2)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.serverName}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--t3)' }}>{r.discordUserId.slice(0, 8)}...</td>
                    <td style={{ color: 'var(--t2)' }}>{r.planName ?? '—'}</td>
                    <td>
                      <Badge
                        status={r.active ? (isExpired ? 'expired' : 'active') : 'expired'}
                        label={r.active ? (isExpired ? 'Expired!' : 'Active') : 'Inactive'}
                      />
                    </td>
                    <td>
                      {r.sessionState ? (
                        <Badge status={r.sessionState} label={r.sessionState.replace(/_/g, ' ')} />
                      ) : (
                        <span style={{ color: 'var(--t3)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--t3)', fontSize: 12 }}>{new Date(r.assignedAt).toLocaleDateString()}</td>
                    <td style={{ color: isExpired ? 'var(--red)' : 'var(--t3)', fontSize: 12, fontWeight: isExpired ? 600 : 400 }}>
                      {new Date(r.expiresAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {r.active && (
                          <>
                            <button
                              className="btn-sm btn-ghost"
                              style={{ fontSize: 11, padding: '3px 8px' }}
                              onClick={() => { setExtendTarget(r); setExtendDays(30); }}
                            >
                              Extend
                            </button>
                            <button
                              className="btn-sm btn-ghost"
                              style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red)' }}
                              disabled={actionLoading}
                              onClick={() => void handleDeactivate(r.id)}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* extend modal */}
      {extendTarget && (
        <Modal open title="Extend Role" onClose={() => setExtendTarget(null)} width={400}>
          <div style={{ padding: '0 0 20px' }}>
            <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 16 }}>
              Extend subscription for <strong>{extendTarget.discordUserId}</strong> on <strong>{extendTarget.serverName}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">Days to Add</label>
              <input
                className="form-input"
                type="number"
                min={1}
                max={365}
                value={extendDays}
                onChange={(e) => setExtendDays(Number(e.target.value))}
              />
              <div className="form-hint">
                Current expiry: {new Date(extendTarget.expiresAt).toLocaleDateString()}
              </div>
            </div>
            <button
              className="btn-sm btn-primary"
              style={{ width: '100%', padding: 12, fontSize: 14, marginTop: 8 }}
              disabled={actionLoading || extendDays < 1}
              onClick={() => void handleExtend()}
            >
              {actionLoading ? 'Extending...' : `Extend by ${extendDays} days`}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
